import { fetchFromApi, API_ENDPOINTS } from '../config/api';

// Cache of artist images discovered via directory listing
let artistImagesCache = null;

/**
 * Fetches the list of all artist images from the server
 * @returns {Promise<string[]>} Array of image filenames in /images/artists/
 */
const fetchArtistImageList = async () => {
  if (artistImagesCache) return artistImagesCache;
  
  try {
    // Fetch the directory listing from the API
    const data = await fetchFromApi(API_ENDPOINTS.artistImages);
    artistImagesCache = data.images || [];
    return artistImagesCache;
  } catch (error) {
    console.warn('Could not fetch artist images list:', error);
  }
  return [];
};

/**
 * Gets a random image URL for a specific artist from local files
 * Matches images where filename starts with artist name (case insensitive)
 * @param {string} artistName - The name of the artist
 * @returns {Promise<string|null>} A promise that resolves with the URL of a random image for the artist
 */
export const getRandomArtistImage = async (artistName) => {
  const allImages = await fetchArtistImageList();
  
  // Normalize artist name for matching (lowercase, no spaces)
  const normalizedName = artistName.toLowerCase().replace(/\s+/g, '');
  
  // Filter images that start with the artist name (case insensitive)
  const artistImages = allImages.filter(filename => {
    const lowerFilename = filename.toLowerCase();
    return lowerFilename.startsWith(normalizedName);
  });
  
  if (artistImages.length === 0) {
    console.warn(`No local images found for artist: ${artistName}`);
    return null;
  }
  
  // Randomly pick one
  const randomIndex = Math.floor(Math.random() * artistImages.length);
  return `/images/artists/${artistImages[randomIndex]}`;
};

/**
 * Preloads an image and returns a promise that resolves when the image is loaded
 * @param {string} imageUrl - The URL of the image to preload
 * @returns {Promise} A promise that resolves when the image is loaded
 */
export const preloadImage = (imageUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(imageUrl);
    img.onerror = () => reject(new Error(`Failed to load image: ${imageUrl}`));
    img.src = imageUrl;
  });
};

export { fetchArtistImageList };
